import { Activity, useEffect, useMemo, useRef, useState } from 'react'
import { Filters } from '@haus-storefront-react/filters'
import { FacetFiltersAccordion } from './components/FacetFiltersAccordion'
import { FacetFiltersDropdown } from './components/FacetFiltersDropdown'
import { AriaLiveRegion, useWidgetReadyAfterInitialLoad, WidgetReadyBoundary } from '@components'
import styles from './Filters.module.scss'
import { type EnabledFilter, type FacetValueFilterType } from '@haus-storefront-react/shared-types'
import { type FiltersAttributes } from './_propsFn'
import classNames from 'classnames'
import { DefaultFilterUrlStrategy } from '@haus-storefront-react/strategies'
import './FiltersSkeleton.css'

const DEFAULT_OPEN_ACCORDIONS = 3
const FILTERS_STRATEGIES = { filtersStoreStrategy: new DefaultFilterUrlStrategy() }

type FacetEnabledFilter = EnabledFilter<FacetValueFilterType>

interface FiltersResolvedContentProps {
  displayType: FiltersAttributes<FacetEnabledFilter>['displayType']
  facetFilters: FacetEnabledFilter[]
  filterAnnouncement: string
  filtersContext: unknown
  onFilterAnnounce: (message: string) => void
  toggleFacetValue: (facetCode: string, valueId: string) => void
}

const getFiltersRootLoadingState = (value: unknown): boolean | undefined => {
  if (typeof value !== 'object' || value === null || !('isLoading' in value)) {
    return undefined
  }

  const loadingValue = Reflect.get(value, 'isLoading')
  return typeof loadingValue === 'boolean' ? loadingValue : undefined
}

const isFacetArrayValue = (value: unknown): value is Array<{ facetCode: string }> => {
  if (!Array.isArray(value)) {
    return false
  }

  return value.every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      'facetCode' in entry &&
      typeof Reflect.get(entry, 'facetCode') === 'string',
  )
}

const getResolvedFacetsFromContext = (value: unknown): Array<{ facetCode: string }> | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined
  }

  const facets = Reflect.get(value, 'facets')
  if (isFacetArrayValue(facets)) {
    return facets
  }

  const facetFilters = Reflect.get(value, 'facetFilters')
  if (isFacetArrayValue(facetFilters)) {
    return facetFilters
  }

  const filters = Reflect.get(value, 'filters')
  if (isFacetArrayValue(filters)) {
    return filters
  }

  return undefined
}

const FiltersResolvedContent = ({
  displayType = 'dropdown',
  facetFilters,
  filterAnnouncement,
  filtersContext,
  onFilterAnnounce,
  toggleFacetValue,
}: FiltersResolvedContentProps) => {
  const loadingFromContext = getFiltersRootLoadingState(filtersContext)
  const resolvedFacetsFromContext = getResolvedFacetsFromContext(filtersContext)
  const [hasRenderableFacetGroups, setHasRenderableFacetGroups] = useState(false)
  const resolvedFacetGroupsCountRef = useRef(0)

  resolvedFacetGroupsCountRef.current = 0

  useEffect(() => {
    if (hasRenderableFacetGroups || resolvedFacetGroupsCountRef.current === 0) {
      return
    }

    setHasRenderableFacetGroups(true)
  }, [filtersContext, hasRenderableFacetGroups])

  const hasConfiguredFacetFilters = facetFilters.length > 0
  const hasResolvedFacetFiltersFromContext = resolvedFacetsFromContext !== undefined
  const hasAnyResolvedFacetFiltersFromContext = (resolvedFacetsFromContext?.length ?? 0) > 0
  const hasVisibleFacetFiltersFromContext =
    resolvedFacetsFromContext?.some((facet) =>
      facetFilters.some((filter) => filter.facetCode === facet.facetCode),
    ) ?? false
  const hasResolvedButNoMatchingFacetFilters =
    hasAnyResolvedFacetFiltersFromContext && !hasVisibleFacetFiltersFromContext
  const hasSettledContent =
    loadingFromContext !== undefined
      ? !loadingFromContext
      : hasResolvedFacetFiltersFromContext || hasRenderableFacetGroups || !hasConfiguredFacetFilters
  const hasVisibleContent =
    !hasConfiguredFacetFilters ||
    hasVisibleFacetFiltersFromContext ||
    hasRenderableFacetGroups ||
    (hasSettledContent && hasResolvedButNoMatchingFacetFilters) ||
    (hasSettledContent && !hasAnyResolvedFacetFiltersFromContext)
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent,
    hasVisibleContent,
    isLoading: loadingFromContext ?? !hasSettledContent,
  })

  let visibleAccordionIndex = 0

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <AriaLiveRegion message={filterAnnouncement} />
      {facetFilters.map((filter) => (
        <Filters.Group key={`probe-${filter.facetCode}`} facetCode={filter.facetCode}>
          {(facet) => {
            if (facet.values.length > 0) {
              resolvedFacetGroupsCountRef.current += 1
            }
            return null
          }}
        </Filters.Group>
      ))}
      <div className={classNames(styles.filtersContent, styles[`filtersContent--${displayType}`])}>
        <Activity mode={displayType === 'dropdown' ? 'visible' : 'hidden'}>
          {facetFilters.map((filter) => (
            <FacetFiltersDropdown
              key={filter.facetCode}
              facetFilter={filter}
              toggleFacetValue={toggleFacetValue}
              onFilterAnnounce={onFilterAnnounce}
            />
          ))}
        </Activity>
        <Activity mode={displayType === 'accordion' ? 'visible' : 'hidden'}>
          {facetFilters.map((filter) => (
            <Filters.Group key={filter.facetCode} facetCode={filter.facetCode}>
              {(facet) => {
                if (facet.values.length === 0) return null

                const currentVisibleIndex = visibleAccordionIndex
                visibleAccordionIndex += 1

                return (
                  <div>
                    <FacetFiltersAccordion
                      facetFilter={filter}
                      facet={facet}
                      toggleFacetValue={toggleFacetValue}
                      onFilterAnnounce={onFilterAnnounce}
                      defaultOpen={currentVisibleIndex < DEFAULT_OPEN_ACCORDIONS}
                      index={currentVisibleIndex}
                    />
                  </div>
                )
              }}
            </Filters.Group>
          ))}
        </Activity>
      </div>
    </WidgetReadyBoundary>
  )
}

export default function FiltersComponent({
  productListIdentifier,
  enabledFilters,
  displayType = 'dropdown',
}: FiltersAttributes<EnabledFilter<FacetValueFilterType>>) {
  const [filterAnnouncement, setFilterAnnouncement] = useState('')
  const facetFilters = useMemo(
    () =>
      (enabledFilters ?? []).filter(
        (filter): filter is FacetEnabledFilter =>
          filter.type === 'facet' || filter.type === undefined,
      ),
    [enabledFilters],
  )

  return (
    <Filters.Root
      enabledFilters={enabledFilters}
      productListIdentifier={productListIdentifier}
      strategies={FILTERS_STRATEGIES}
    >
      {(ctx) => (
        <FiltersResolvedContent
          displayType={displayType}
          facetFilters={facetFilters}
          filterAnnouncement={filterAnnouncement}
          filtersContext={ctx}
          onFilterAnnounce={setFilterAnnouncement}
          toggleFacetValue={ctx.toggleFacetValue}
        />
      )}
    </Filters.Root>
  )
}
